import { prompt } from "../helpers/prompt";
import { fileNameSelectMask, safeName } from "../helpers/common";

export default class Operations {
	constructor(app, state) {
		this.app = app;
		this.state = state;

		this.initEvents();
	}

	backend() {
		return this.app.getService("backend");
	}

	local() {
		return this.app.getService("local");
	}

	initEvents() {
		this.app.attachEvent("app:action", (name, info) => {
			switch (name) {
				case "open":
					this.open(info);
					break;
				case "download":
					this.download(info);
					break;
				case "edit":
					this.edit(info);
					break;
				case "delete":
					this.remove(info);
					break;
				case "makefile":
					this.makeFile(info);
					break;
				case "makedir":
					this.makeFolder(info);
					break;
				case "rename":
					this.rename(info);
					break;
				case "copy":
				case "cut":
					this.addToClipboard(name);
					break;
				case "paste":
					this.paste();
					break;
				case "locate":
					this.goUp(info);
			}
		});
	}

	write(id, content) {
		return this.backend()
			.writeText(id, content)
			.then(data =>
				this.local().updateFile(id, { size: data.size, date: data.date })
			);
	}

	read(id) {
		return this.backend().readText(id);
	}

	makeFile(name) {
		const id = this.state.path || "/";
		this.backend()
			.makefile(id, name)
			.then(res => {
				if (!res.invalid) this.local().addFile(id, res);
			});
	}

	makeFolder(name) {
		const id = this.state.path || "/";
		this.backend()
			.makedir(id, name)
			.then(res => {
				if (!res.invalid) this.local().addFile(id, res);
			});
	}

	edit(f) {
		const state = this.state;

		if (!f) f = state.selectedItem;
		const file = f[0];
		if (f.length && file.type === "code")
			this.app.show("/editor", {
				params: {
					file,
					state,
				},
			});
	}

	open(f) {
		if (!f) f = this.state.selectedItem;
		for (let i = 0; i < f.length; ++i) {
			if (f[i].type != "folder") {
				window.open(this.backend().directLink(f[i].id), "_blank");
			}
		}
	}

	download(f) {
		if (!f) f = this.state.selectedItem;
		window.open(this.backend().directLink(f[0].id, true), "_self");
	}

	remove(files) {
		const state = this.state;

		files = this.extractIds(files || state.selectedItem);
		if (!files.length) return;

		const _ = this.app.getService("locale")._;
		return webix
			.confirm({
				title: _("Delete files"),
				text: this.removeConfirmMessage(state.selectedItem),
				css: "webix_fmanager_confirm",
			})
			.then(() => {
				return this.app
					.getService("progress")
					.files(_("Deleting..."), files, f => {
						return this.backend()
							.remove(f)
							.then(res => {
								if (!res.invalid) {
									this.local().deleteFile(f);
									if (state.path === f) {
										state.path = "/";
									}
									// reset inactive path in 'double'
									this.app.callEvent("pathChanged", [f, "/"]);
									this.app.callEvent("reload:fs:stats", []);
								}
							});
					});
			})
			.then(() => {
				// relaunch search
				if (state.search) {
					state.search = state.search + " ";
				}
			});
	}

	removeConfirmMessage(files) {
		const _ = this.app.getService("locale")._;
		let message = `<div class="question">${_(
			"Are you sure you want to delete"
		)} ${files.length > 1 ? _("these items:") : _("this item:")}</div>`;

		let i = 0;
		const icon = "&#9679;&nbsp;";
		for (const limit = files.length < 6 ? files.length : 5; i < limit; ++i) {
			message += `<div class="item">${icon}${files[i].value}</div>`;
		}
		if (i < files.length) {
			message += `<div>${icon}${_("and")} ${files.length - i} ${_(
				"more file(s)"
			)}</div>`;
		}

		return message;
	}

	rename(f) {
		const state = this.state;
		const file = f ? f[0] : state.selectedItem[0];
		if (!file) return;

		const _ = this.app.getService("locale")._;
		const oldId = file.id;

		prompt({
			text: _("Enter a new name"),
			button: _("Rename"),
			value: file.value,
			selectMask: file.type !== "folder" ? fileNameSelectMask : null,
		}).then(name => {
			name = safeName(name);
			if (name && name !== file.value)
				this.backend()
					.rename(oldId, name)
					.then(res => {
						this.local().updateFile(
							oldId,
							{ value: res.id.split("/").pop() },
							res.id
						);

						if (file.type === "folder")
							this.reloadBranch(res.id).then(() => {
								// reset active path in 'list'/'cards' if folder was renamed
								if (state.path === oldId) {
									state.path = res.id;
								}
								// reset inactive path in 'double'
								this.app.callEvent("pathChanged", [oldId, res.id]);
							});

						// relaunch search
						if (state.search) {
							state.search = state.search + " ";
						}
					});
		});
	}

	reloadBranch(id) {
		const hierarchy = this.local().hierarchy;
		return this.app
			.getService("backend")
			.folders(id)
			.then(data => {
				const toRemove = [];
				hierarchy.data.eachChild(id, obj => toRemove.push(obj.id));
				hierarchy.parse({ parent: id, data });
				hierarchy.remove(toRemove);
			});
	}

	copy(files, targetFolder) {
		if (!files.length) return;
		const local = this.local();
		const _ = this.app.getService("locale")._;

		return this.app.getService("progress").files(_("Copying..."), files, f => {
			return this.backend()
				.copy(f, targetFolder)
				.then(res => {
					if (!res.invalid) {
						local.addFile(targetFolder, res);
					}
				});
		});
	}

	move(files, targetFolder) {
		if (!files.length || !targetFolder) return;
		const local = this.local();

		// prevent move to the existing location
		const tfs = local.files(targetFolder, true);
		if (tfs) files = files.filter(a => !tfs.exists(a));
		// prevent moving folder into itself
		files = files.filter(a => a != targetFolder);
		if (!files.length) return;

		const _ = this.app.getService("locale")._;
		return this.app.getService("progress").files(_("Moving..."), files, f => {
			return this.backend()
				.move(f, targetFolder)
				.then(res => {
					if (!res.invalid) {
						local.deleteFile(f);
						local.addFile(targetFolder, res);

						if (this.state.path === f) {
							this.state.path = res.id;
						}
						// reset inactive path in 'double'
						this.app.callEvent("pathChanged", [f, res.id]);
					}
				});
		});
	}

	extractIds(files) {
		if (!files.length || typeof files[0] == "string") return files;

		const ids = [];
		for (let i = 0; i < files.length; ++i) {
			// for actions from context menu, filter out "back to parent"
			if (files[i].value !== "..") ids.push(files[i].id);
		}
		return ids;
	}

	addToClipboard(mode) {
		const ids = this.extractIds(this.state.selectedItem);
		if (ids.length)
			this.state.clipboard = {
				files: ids,
				mode: mode,
			};
	}

	clearClipboard() {
		this.state.clipboard = null;
	}

	paste() {
		const state = this.state;

		if (!state.clipboard) return;

		if (state.clipboard.mode === "copy") {
			this.copy(state.clipboard.files, state.path);
		} else if (state.clipboard.mode === "cut") {
			this.move(state.clipboard.files, state.path);
		}
		this.clearClipboard();
	}

	goUp(files) {
		const state = this.state;
		if (state.search) {
			const file = files ? files[0] : state.selectedItem[0];
			if (!file) return;
			const up = file.id.split("/");

			const path = "/" + up.slice(1, up.length - 1).join("/");
			state.$batch({
				search: "",
				searchStats: null,
				path: path,
			});
		}
	}
}
